import React, {useState} from "react";

import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  SelectChangeEvent,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ActionButtonGroup from "../../../components/ActionButtonGroup";
import MWSkeleton from "../../../components/MWSkeleton";
import SubTitle from "../../../components/SubTitle";
import {useGetQuery, useRemoveMutation} from "../../../hooks/useQueryHooks";
import {curriculumListActionButton} from "../../../models/clientModels/data/actionButtonData";
import {ResponseList, ResponseRemove} from "../../../models/clientModels/interfaces/Response";
import {
  CurriculumListParams,
  EDCurriculum,
  initialCurriculumListParams,
} from "../../../models/serverModels/education/EDCurriculum";
import {FilterGroup, SubMenuGroup} from "../../../styledComponents/StyledListContainer";

import {
  EDCategory,
  initialEDCategoryListParams,
} from "../../../models/serverModels/education/EDCategory";

import * as curriculumListApi from "../../../api/educationApi/curriculumListApi";

import * as categoryListApi from "../../../api/educationApi/educationCategoryApi";
import {curriculumListTableHead} from "../../../models/clientModels/data/tableHeaderData";
import * as Params from "../../../models/clientModels/interfaces/apiParams";

import {TextField} from "@mui/material";
import {LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import {useQueryClient} from "react-query";
import {Link, useNavigate} from "react-router-dom";
import {useRecoilValue, useSetRecoilState} from "recoil";
import {useDepartmentList} from "../../../hooks/useDepartmentList";
import {alertStateAtom} from "../../../Recoil/Atom/AlertAtom";
import {tokenSelector} from "../../../Recoil/Atom/authState";
import {PaginationContainer} from "../../../styledComponents/StyledPagination";
import {StyledTableContainer, StyledTableHead} from "../../../styledComponents/StyledTable";
import ko from "date-fns/locale/ko";

const ListContainer = styled(Box)(({theme}) => ({
  minWidth: "1700px",
}));

type CategoryType = {
  value: number;
  label: string;
};

const CurriculumList = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const tokenState = useRecoilValue(tokenSelector);

  const [page, setPage] = useState<number>(1);
  const [filter, setFilter] = useState<CurriculumListParams>({
    ...initialCurriculumListParams,
    token: tokenState,
  });
  const queryString = Object.entries(filter)
    .map((e) => e.join("="))
    .join("&");

  const setAlertState = useSetRecoilState(alertStateAtom);

  const [middleCategory, setMiddleCategory] = useState<CategoryType[]>([]);
  const [subCategory, setSubCategory] = useState<CategoryType[]>([]);

  const {departmentList} = useDepartmentList();

  // region: Query (교육과정 카테고리 리스트)
  const {data: category} = useGetQuery<ResponseList<EDCategory>>({
    key: ["EducationCategoryListAll", {...initialEDCategoryListParams, token: tokenState}],
    api: () => categoryListApi.getListAll({...initialEDCategoryListParams, token: tokenState}),
  });

  // region: Query (교육과정리스트)
  const {
    data: curriculum,
    isLoading,
    isFetching,
  } = useGetQuery<ResponseList<EDCurriculum>>({
    key: ["CurriculumList", {...filter, token: tokenState}],
    api: () => curriculumListApi.getList({...filter, token: tokenState}),
    // options: {
    //   cacheTime: 30000,
    // },
  });

  const {mutate: removeMutate} = useRemoveMutation<ResponseRemove, Params.Remove>({
    api: curriculumListApi.remove,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries("CurriculumList");
      },
    },
  });

  const departmentHandler = (event: SelectChangeEvent<number>) => {
    setFilter({
      ...filter,
      page: 1,
      [event.target.name]: event.target.value,
    });
  };

  // region: function (교육과정 필터링)
  const mainCategoryHandler = (event: SelectChangeEvent<number>) => {
    setMiddleCategory([]);
    setFilter({
      ...filter,
      filterDepth2UID: 0,
      filterDepth3UID: 0,
      page: 1,
      [event.target.name]: event.target.value,
    });
    category?.items.filter(
      (item) =>
        item.uid === event.target.value &&
        item.subCategories.map((item) =>
          setMiddleCategory((prev) => [...prev, {value: item.uid, label: item.name}])
        )
    );
  };

  const middleCategoryHandler = (event: SelectChangeEvent<number>) => {
    setSubCategory([]);
    setFilter({...filter, filterDepth3UID: 0, page: 1, [event.target.name]: event.target.value});
    category?.items.map((item) =>
      item.subCategories.filter(
        (item) =>
          item.uid === event.target.value &&
          item.subCategories.map((item) =>
            setSubCategory((prev) => [...prev, {value: item.uid, label: item.name}])
          )
      )
    );
  };
  const subCategoryHandler = (event: SelectChangeEvent<number>) => {
    setFilter({
      ...filter,
      page: 1,
      [event.target.name]: event.target.value,
    });
  };

  const startDateHandler = (newValue: Date | null) => {
    setFilter({
      ...filter,
      page: 1,
      searchStartDate: newValue,
    });
  };

  const endDateHandler = (newValue: Date | null) => {
    setFilter({
      ...filter,
      page: 1,
      searchEndDate: newValue,
    });
  };

  const paginationHandleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setFilter({...filter, page: value});
    setPage(value);
  };

  return (
    <ListContainer>
      <SubTitle subTitle="교육과정 목록" />
      <SubMenuGroup>
        <FilterGroup>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ko}>
            <Box>
              <DatePicker
                inputFormat={"yyyy년MM월dd일"}
                label="시작일"
                value={filter.searchStartDate}
                onChange={startDateHandler}
                renderInput={(params) => <TextField size="small" {...params} />}
              />
            </Box>
            <Box>
              <DatePicker
                inputFormat={"yyyy년MM월dd일"}
                label="종료일"
                value={filter.searchEndDate}
                onChange={endDateHandler}
                renderInput={(params) => <TextField size="small" {...params} />}
              />
            </Box>
          </LocalizationProvider>
          <FormControl>
            <InputLabel id="departmentUID">대상부서</InputLabel>
            <Select
              name="departmentUID"
              id="departmentUID"
              label="대상부서"
              size="small"
              value={filter?.departmentUID}
              onChange={departmentHandler}
            >
              <MenuItem value={0}>전체</MenuItem>
              {departmentList?.items.map((item) => (
                <MenuItem key={item.uid} value={item.uid}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <InputLabel id="mainCategory">대분류</InputLabel>
            <Select
              name="filterDepth1UID"
              id="mainCategory"
              label="대분류"
              size="small"
              value={filter?.filterDepth1UID}
              onChange={mainCategoryHandler}
            >
              <MenuItem value={0}>전체</MenuItem>
              {category?.items.map((item) => (
                <MenuItem key={item.uid} value={item.uid}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel id="middleCategory">중분류</InputLabel>
            <Select
              name="filterDepth2UID"
              id="middleCategory"
              label="중분류"
              value={filter?.filterDepth2UID}
              size="small"
              onChange={middleCategoryHandler}
            >
              <MenuItem value={0}>전체</MenuItem>
              {middleCategory?.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel id="subCategory">세분류</InputLabel>
            <Select
              name="filterDepth3UID"
              id="subCategory"
              label="세분류"
              value={filter?.filterDepth3UID}
              size="small"
              onChange={subCategoryHandler}
            >
              <MenuItem selected value={0}>
                전체
              </MenuItem>
              {subCategory?.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box>
            <Button
              variant="contained"
              color="success"
              size="small"
              sx={{whiteSpace: "nowrap"}}
              onClick={() => {
                setFilter(initialCurriculumListParams);
                setMiddleCategory([]);
                setSubCategory([]);
              }}
            >
              초기화
            </Button>
          </Box>
        </FilterGroup>
        <ActionButtonGroup data={curriculumListActionButton(queryString)} />
      </SubMenuGroup>
      <StyledTableContainer>
        <Table>
          <StyledTableHead>
            <TableRow>
              {curriculumListTableHead.map((item, index) => (
                <TableCell key={index} align="center">
                  {item}
                </TableCell>
              ))}
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {isLoading && isFetching ? (
              <MWSkeleton row={12} column={12} />
            ) : (
              curriculum?.items?.map((item, index) => {
                const depth1 = category?.items.find((id) => id.uid === item.category1UID);
                const depth2 = depth1?.subCategories.find((id) => id.uid === item.category2UID);
                const depth3 = depth2?.subCategories.find((id) => id.uid === item.category3UID);
                return (
                  <TableRow key={item.uid}>
                    <TableCell align="center">
                      {page === 1
                        ? index + 1
                        : (page - 1) * initialEDCategoryListParams.rows! + index + 1}
                    </TableCell>
                    <TableCell align="center">{`${new Date(item?.originDate).getFullYear()}년 ${
                      new Date(item?.originDate).getMonth() + 1
                    }월`}</TableCell>
                    <TableCell align="center">{depth1?.name}</TableCell>
                    <TableCell align="center">{depth2?.name}</TableCell>
                    <TableCell align="center">{depth3?.name}</TableCell>
                    <TableCell align="center">
                      {item.textBookFile === undefined ||
                      item.textBookFile === null ||
                      item.textBookFile === "" ? (
                        "---"
                      ) : (
                        <Link to="">다운로드</Link>
                      )}
                    </TableCell>
                    <TableCell align="center" width="30%">
                      <Box sx={{position: "relative", display: "flex", alignItems: "center"}}>
                        <Typography
                          sx={{
                            width: "100%",
                            position: "absolute",
                            left: 0,
                            top: 0,
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            wordBreak: "break-all",
                            fontSize: "14px",
                          }}
                        >
                          {departmentList?.items.map((value) =>
                            item.departments.map((v, idx) =>
                              parseInt(v) === value.uid
                                ? idx === item.departments.length - 1
                                  ? value.name
                                  : value.name + ", "
                                : null
                            )
                          )}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Link to={`/Education/EditExam/${item.uid}`}>문항관리</Link>
                    </TableCell>
                    <TableCell align="center">
                      <Link
                        to={`/Education/ExamRank/${item.uid}`}
                        state={{departments: item.departments}}
                      >
                        {item.allTrialCount} / {item.allExamerCount}
                      </Link>
                    </TableCell>
                    <TableCell align="center">
                      <Link to={`/Education/ExamReport/${item.uid}`}>{item.allScoreAverage}</Link>
                    </TableCell>
                    <TableCell align="center">
                      <Link to={`/Education/ExamQRCode/${item.uid}`}>QR Code</Link>
                    </TableCell>
                    <TableCell align="center" width="15%">
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        style={{marginRight: "6px"}}
                        onClick={() => {
                          navigate(`/education/updateCurriculum/${item.uid}`);
                        }}
                      >
                        편집
                      </Button>
                      <Button
                        variant="contained"
                        color="info"
                        size="small"
                        style={{marginRight: "6px"}}
                        onClick={() => {
                          navigate(`/education/CurriculumReport/${item.uid}`);
                        }}
                      >
                        보고서
                      </Button>
                      <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={() => {
                          setAlertState({
                            toggle: true,
                            message: "교육과정 목록을 삭제하시겠습니까?",
                            execution: () => removeMutate({token: tokenState, id: item.uid}),
                          });
                        }}
                      >
                        삭제
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>
      {/* 페이지네이션 */}
      <PaginationContainer>
        <Pagination
          count={curriculum?.maxPage}
          page={page}
          onChange={paginationHandleChange}
          variant="outlined"
          color="primary"
          shape="rounded"
        />
      </PaginationContainer>
    </ListContainer>
  );
};

export default CurriculumList;
